import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PiArrowCounterClockwiseLight } from 'react-icons/pi';
import Table from '../shared/TailwindComponents/Table';
import { listClients, updateClient } from '../../store/actions/ClientActions';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';

interface Props {
  /** How many deactivated contacts there are - added to the Archived count. */
  onCountChange?: (count: number) => void;
  /** A contact was reactivated, so the active list should refetch. */
  onReactivated?: () => void;
}

/**
 * Deactivated contacts, in the Archived view (decided 2 Nov 2026).
 *
 * Deactivating keeps a contact (it is not deleted, so it is not in the recycle
 * bin) but takes it out of the active list - which left it nowhere at all.
 * Archived now shows them above the recycle bin, with Reactivate.
 */
const DeactivatedContacts: React.FC<Props> = ({ onCountChange, onReactivated }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await listClients(1, 200, { is_active: false });
      const list = res?.success && Array.isArray(res.clients) ? res.clients : [];
      setRows(list);
      onCountChange?.(res?.pagination?.total ?? list.length);
    } catch {
      setRows([]);
      onCountChange?.(0);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => { load(); }, [load]);

  const reactivate = async (row: any) => {
    try {
      await updateClient(row.uuid, { is_active: true });
      dispatch(showFeedbackModal({ showModal: true, modalType: 'success', message: t('Contact reactivated'), duration: 3000 }));
      onReactivated?.();
      load();
    } catch (e: any) {
      dispatch(showFeedbackModal({ showModal: true, modalType: 'error', message: e?.message || t('Failed to reactivate contact'), duration: 3000 }));
    }
  };

  // Nothing until it has loaded and found someone - no empty "(0)" band
  // above the recycle bin.
  if (loading || rows.length === 0) return null;

  return (
    <section className="flex-shrink-0 border-b border-hairline-soft" data-testid="deactivated-contacts">
      <h3 className="px-4 pt-3 pb-1 text-sm font-medium text-fg-strong">
        {t('Deactivated')} <span className="text-fg-muted tabular-nums">({rows.length})</span>
      </h3>
      <div className="max-h-80 flex flex-col min-h-0">
        <Table
          tableId="deactivated-contacts"
          columns={['Name', 'Email', 'Actions']}
          data={rows.map((c) => ({
            id: c.uuid || c.id,
            uuid: c.uuid,
            Name: c.name,
            Email: c.contact_email || '—',
          }))}
          isLoading={loading}
          skeletonRows={2}
          onRowClick={(row) => history.push(`/contacts/${row.uuid}`)}
          actions={[{
            label: t('Reactivate'),
            onClick: reactivate,
            icon: <PiArrowCounterClockwiseLight className="h-4 w-4" />,
          }]}
        />
      </div>
    </section>
  );
};

export default DeactivatedContacts;
